import { Alert, useTheme } from "@mui/material";
import Button from "@mui/material/Button";
import Card from "components/elements/Card";
import useExchangesMetadata from "hooks/useExchangesMetadata";
import useExchangesMetadataGlobal from "hooks/useExchangesMetadataGlobal";
import { ErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import { IDcaBotRun, IInvestmentChart, IPriceChart, IRunsListOrder } from "types/types";

import BaseCurrencyAmountChart from "./BaseCurrencyAmountChart";
import Chart from "./chart";
import ChartWrapper from "./ChartWrapper";
import ErrorBaseCurrencyAmountChartFallback from "./ErrorBaseCurrencyAmountChartFallback";
import ErrorFallback from "./ErrorFallback";
import ErrorPriceChartFallback from "./ErrorPriceChartFallback";
import PriceChart from "./priceChart";

interface Props {
  chartData?: IInvestmentChart;
  priceChartData?: IPriceChart;
  dcaBotRuns: IDcaBotRun[];
  baseCurrency: string;
  counterCurrency: string;
  currencyPair: string;
  exchangeEnum?: string;
  targetValuePrice?: number;
  baseCurrencyAmountTargets: number[];
  setTargetValueModalOpen: () => void;
  setBaseCurrencyAmountTargetsModalOpen: () => void;
}

const ChartHistory = ({
  chartData,
  priceChartData,
  dcaBotRuns,
  baseCurrency,
  counterCurrency,
  currencyPair,
  exchangeEnum,
  targetValuePrice,
  baseCurrencyAmountTargets,
  setTargetValueModalOpen,
  setBaseCurrencyAmountTargetsModalOpen,
}: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { baseCurrDisplayedScale, counterCurrDisplayedScale } = useExchangesMetadata(exchangeEnum ?? "", currencyPair);
  const { baseCurrShortDisplayedScale, counterCurrShortDisplayedScale } = useExchangesMetadataGlobal(currencyPair);

  const orders: IRunsListOrder[] = [];
  dcaBotRuns.forEach((item: IDcaBotRun) => {
    const filledOrders = item.orders.filter((order) => order.status === "FILLED");
    filledOrders.forEach((filledOrder) => orders.push(filledOrder));
  });
  const ordersSorted = orders.sort((a, b) => Date.parse(a.updatedAt) - Date.parse(b.updatedAt));
  const dataAvailable = ordersSorted.length >= 2;

  return (
    <Card
      header={t("chartHistory.title")}
      sx={{ mb: 0 }}
      actions={
        dataAvailable ? (
          <Button color="info" onClick={setTargetValueModalOpen} variant="outlined" size="small">
            {t("chartHistory.targetValue.ctaEdit")}
          </Button>
        ) : undefined
      }>
      {dataAvailable && chartData ? (
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <Chart
            chartData={chartData}
            baseCurrency={baseCurrency}
            counterCurrency={counterCurrency}
            baseCurrDisplayedScale={baseCurrDisplayedScale}
            counterCurrDisplayedScale={counterCurrDisplayedScale}
            targetValuePrice={targetValuePrice}
            counterCurrShortDisplayedScale={counterCurrShortDisplayedScale}
          />
        </ErrorBoundary>
      ) : (
        <Alert severity="info">{t("chartHistory.notEnoughData")}</Alert>
      )}
      <ChartWrapper
        title={t("priceChart.title")}
        alertMessage={t("priceChart.notEnoughData")}
        FallbackComponent={ErrorPriceChartFallback}>
        {priceChartData && (
          <PriceChart
            chartData={priceChartData}
            counterCurrency={counterCurrency}
            counterCurrDisplayedScale={counterCurrDisplayedScale}
            counterCurrShortDisplayedScale={counterCurrShortDisplayedScale}
          />
        )}
      </ChartWrapper>
      <ChartWrapper title={t("chartHistory.baseCurrencyAmountChart.title", { baseCurrency })} FallbackComponent={ErrorBaseCurrencyAmountChartFallback}>
        {dataAvailable && chartData && (
          <BaseCurrencyAmountChart
            chartData={chartData}
            baseCurrency={baseCurrency}
            baseCurrDisplayedScale={baseCurrDisplayedScale}
            baseCurrShortDisplayedScale={baseCurrShortDisplayedScale}
            baseCurrencyAmountTargets={baseCurrencyAmountTargets}
            onEditTargets={setBaseCurrencyAmountTargetsModalOpen}
          />
        )}
      </ChartWrapper>
    </Card>
  );
};

export default ChartHistory;
